/**
 * Calculates a dynamic font size for captions based on video dimensions.
 * It adjusts the size to be larger for portrait videos and caps the size
 * within a reasonable min/max range.
 *
 * @param videoWidth - The width of the video canvas.
 * @param videoHeight - The height of the video canvas.
 * @param captionWidth - The width of the caption container element.
 * @returns The calculated font size in pixels.
 */
export const generateDynamicFontSize = (
  videoWidth: number,
  videoHeight: number,
  captionWidth: number
): number => {
  const isPortrait = videoHeight > videoWidth;

  let baseFontSize: number;

  if (isPortrait) {
    baseFontSize = Math.round(videoWidth * 0.07);
  } else {
    const baseCharactersPerLine = 30;
    baseFontSize = Math.round(captionWidth / baseCharactersPerLine);
  }

  const minFontSize = 16;
  const maxFontSize = isPortrait
    ? Math.round(videoWidth * 0.08)
    : Math.round(videoWidth * 0.045);

  return Math.max(minFontSize, Math.min(baseFontSize, maxFontSize));
};

/**
 * Calculates the position and size of a caption container based on video dimensions and format.
 * @param video - The video object containing format and dimension details.
 * @param videoWidth - The width of the video canvas.
 * @param videoHeight - The height of the video canvas.
 * @param captionWidthPercentage - The desired width of the caption as a percentage of video width.
 * @param captionHeightRatio - The desired height/width ratio of the caption container.
 * @returns An object with position {x, y} and size {width, height}.
 */
export const generateCaptionDetails = (
  video: any,
  videoWidth: number,
  videoHeight: number,
  captionWidthPercentage: number = 0.75,
  captionHeightRatio: number = 0.2
) => {
  let bottomMargin: number = 0;

  switch (video.format) {
    case "word":
      if (
        video.details?.height &&
        video.details.width &&
        video.details.height > video.details.width
      ) {
        bottomMargin = 0.225;
      } else {
        bottomMargin = 0.12;
      }
      break;
    case "extra-short":
      if (
        video.details?.height &&
        video.details.width &&
        video.details.height > video.details.width
      ) {
        bottomMargin = 0.25;
      } else {
        bottomMargin = 0.125;
      }
      break;
    case "short":
      if (
        video.details?.height &&
        video.details.width &&
        video.details.height > video.details.width
      ) {
        bottomMargin = 0.275;
      } else {
        bottomMargin = 0.135;
      }
      break;
    default:
      if (
        video.details?.height &&
        video.details.width &&
        video.details.height > video.details.width
      ) {
        bottomMargin = 0.35;
      } else {
        bottomMargin = 0.1;
      }
      break;
  }
  const captionWidth = Math.round(videoWidth * captionWidthPercentage);
  const captionHeight = Math.round(captionWidth * captionHeightRatio);

  const x = Math.round((videoWidth - captionWidth) / 2);

  const bottomSpacing = Math.round(videoHeight * bottomMargin);
  const y = videoHeight - captionHeight - bottomSpacing;

  return {
    position: { x, y },
    size: { width: captionWidth, height: captionHeight },
  };
};
